import React, { FC, useEffect, useState } from "react";
import {
  Input,
  Button,
  Col,
  Row,
  DatePicker,
  Select,
  Checkbox,
  Radio,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { DownOutlined } from "@ant-design/icons";
import _ from "lodash";
import moment from "moment";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import TimeInput from "../TimeInput";

const { Option } = Select;

const { TextArea } = Input;

const EventInput: FC<{
  value?: () => void;
  onChange?: any;
}> = (props) => {
  const { value, onChange } = props;
  const [event, setEvent] = useState<any>({
    title: "",
    repeat: false,
    repeatAfter: 1,
    repeatFrequency: "daily",
    repeatEnds: "never",
    repeatUntil: undefined,
    date: undefined,
    time: undefined,
    notifyTime: undefined,
    description: "",
    location: {
      attributes: {
        title: undefined,
        address: undefined,
      },
      coordinates: undefined,
    },
  });

  const updateEvent = (key, value) => {
    setEvent((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    const data = { ...event, [key]: value };
    if (data.date) {
      data.date = new Date(data.date).getTime();
    }
    if (data.time) {
      data.time = new Date(data.time).getTime();
    }
    if (data.notifyTime) {
      data.notifyTime = new Date(data.notifyTime).getTime();
    }
    if (data.repeatUntil) {
      data.repeatUntil = new Date(data.repeatUntil).getTime();
    }
    onChange(data);
  };

  useEffect(() => {
    if (value) {
      const tempEvent: any = { ...value };
      if (tempEvent.date) {
        tempEvent.date = moment(tempEvent.date).format("MM/DD/YYYY HH:mm:ss");
      }
      if (tempEvent.time) {
        tempEvent.time = moment(tempEvent.time).format("MM/DD/YYYY HH:mm:ss");
      }
      if (tempEvent.notifyTime) {
        tempEvent.notifyTime = moment(tempEvent.notifyTime).format(
          "MM/DD/YYYY HH:mm:ss"
        );
      }
      if (tempEvent.repeatUntil) {
        tempEvent.repeatUntil = moment(tempEvent.repeatUntil).format(
          "MM/DD/YYYY HH:mm:ss"
        );
      }
      setEvent(tempEvent);
    }
  }, [value]);

  return (
    <React.Fragment>
      <Row>
        <Col span={24}>
          <Input
            value={event.title}
            onChange={(e) => updateEvent("title", e.target.value)}
            placeholder="Event Title"
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col span={24}>
          <TextArea
            autoSize={{ minRows: 4, maxRows: 6 }}
            placeholder="Description"
            value={event.description}
            onChange={(e) => {
              updateEvent("description", e.target.value);
            }}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col span={12} className="pr-2">
          <div className="datetime-item">
            <img src="/images/date.png" />
            <DatePicker
              value={
                event.date
                  ? moment(event.date, "MM/DD/YYYY HH:mm:ss")
                  : undefined
              }
              onChange={(date: moment.Moment | null) => {
                updateEvent("date", date?.format("MM/DD/YYYY HH:mm:ss"));
              }}
              placeholder="Date"
              suffixIcon={<DownOutlined />}
            />
          </div>
        </Col>
        <Col span={12} className="pl-2">
          <div className="datetime-item">
            <img src="/images/time.png" />
            <TimeInput
              name="time"
              value={
                event.time
                  ? moment(event.time, "MM/DD/YYYY HH:mm:ss")
                  : undefined
              }
              onChange={(time) => {
                updateEvent(
                  "time",
                  moment(time)?.format("MM/DD/YYYY HH:mm:ss")
                );
              }}
            />
          </div>
        </Col>
      </Row>

      <Row className="mt-3">
        <Checkbox.Group
          options={["Repeat every"]}
          onChange={(values) => {
            updateEvent("repeat", values.indexOf("Repeat every") !== -1);
          }}
          value={event.repeat ? ["Repeat every"] : []}
        />
      </Row>
      {event.repeat && (
        <React.Fragment>
          <Row className="mt-1">
            <Col span={12} className="pr-2">
              <Input
                value={event.repeatAfter}
                onChange={(e) => {
                  updateEvent("repeatAfter", e.target.value);
                }}
                type="number"
                min={1}
              ></Input>
            </Col>
            <Col span={12} className="pl-2">
              <div className="datetime-item prefix-select-wrapper">
                <img src="/images/refresh.png" />
                <Select
                  value={event.repeatFrequency}
                  className="w-full"
                  placeholder="Repeat"
                  onChange={(values) => {
                    updateEvent("repeatFrequency", values);
                  }}
                >
                  <Option value="daily">day</Option>
                  <Option value="weekly">week</Option>
                  <Option value="monthly">month</Option>
                </Select>
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col span={24}>
              <span className="ant-checkbox-wrapper">Ends</span>
            </Col>
            <Radio.Group
              value={event.repeatEnds}
              onChange={(e) => {
                updateEvent("repeatEnds", e.target.value);
              }}
              className="plain-radios"
            >
              <Radio value={"never"} className="green-radio">
                Never
              </Radio>
              <Radio value={"on"} className="green-radio">
                On
              </Radio>
            </Radio.Group>

            {event.repeatEnds === "on" && (
              <div className="datetime-item mt-1">
                <DatePicker
                  value={
                    event.repeatUntil
                      ? moment(event.repeatUntil, "MM/DD/YYYY HH:mm:ss")
                      : undefined
                  }
                  onChange={(date: moment.Moment | null) => {
                    updateEvent(
                      "repeatUntil",
                      date?.format("MM/DD/YYYY HH:mm:ss")
                    );
                  }}
                  placeholder="Date"
                  suffixIcon={<DownOutlined />}
                />
              </div>
            )}
          </Row>
        </React.Fragment>
      )}
      <Row className="mt-3">
        <Col span={12} className="pr-2">
          <div className="datetime-item">
            <img src="/images/warning.png" />
            <TimeInput
              name="Notify Time"
              value={
                event.notifyTime
                  ? moment(event.notifyTime, "MM/DD/YYYY HH:mm:ss")
                  : undefined
              }
              onChange={(time) => {
                updateEvent(
                  "notifyTime",
                  moment(time)?.format("MM/DD/YYYY HH:mm:ss")
                );
              }}
            />
          </div>
        </Col>
        <Col span={12} className="pl-2">
          <Select
            value={event.notifyVia}
            className="w-full"
            placeholder="Notify via"
            onChange={(values) => {
              updateEvent("notifyVia", values);
            }}
            options={[
              { label: "Push", value: "Push" },
              { label: "SMS", value: "SMS" },
              { label: "Email", value: "Email" },
            ]}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col span={24}>
          <div className="datetime-item prefix-select-wrapper">
            <img src="/images/location.png" />
            <GooglePlacesAutocomplete
              autocompletionRequest={{}}
              selectProps={{
                value: event?.location?.attributes?.value?.place_id
                  ? event?.location?.attributes
                  : undefined,
                onChange: (place: any) => {
                  geocodeByPlaceId(place.value.place_id)
                    .then((results) => {
                      if (results[0].geometry) {
                        let location = {
                          attributes: {
                            ...place,
                            address: place.label,
                          },
                          coordinates: {
                            lat: results[0].geometry.location.lat(),
                            long: results[0].geometry.location.lng(),
                          },
                        };
                        updateEvent("location", location);
                      }
                    })
                    .catch((error) => console.error(error));
                },
                isClearable: true,
                placeholder: "Search map",
                styles: {
                  input: (provided: any) => ({
                    ...provided,
                    fontFamily: "Nunito-Regular",
                  }),
                  control: () => ({
                    backgroundColor: "hsl(218deg 33% 95%)",
                    display: "flex",
                  }),
                  container: (provided, state) => ({
                    ...provided,
                    width: "100%",
                  }),
                  placeholder: (provided, state) => ({
                    ...provided,
                    color: "#000",
                    fontSize: 16,
                    padding: 8,
                  }),
                  singleValue: (provided, state) => ({
                    ...provided,
                    color: "#000",
                    fontSize: 16,
                    padding: 8,
                  }),
                },
              }}
              apiKey={"AIzaSyDNIyoSDV1wnJVW8wdHkiNG-eZ2bMcq0iY"}
            />
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default EventInput;
